// Copyright (c) 2023-2024, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com
// See license file.
import type { Dentist, Actor, HealthCenter, Template } from "@/types";
const templateRegexp = /\$[A-Z0-9_\s$]+\$/g;

// Finds all the used macros in a list of templates. Macros look like $TEXT TEXT$.
export function allUsedMacros(list: Array<Template>): Array<string> {
  const macros = new Set<string>();
  list.forEach((template) => {
    const matches = template.content.match(templateRegexp);
    if (matches != null) {
      matches.forEach((match) => {
        macros.add(match);
      });
    }
  });
  return Array.from(macros);
}

function extractInfo(subject: Actor | HealthCenter | Dentist): {
  name: string;
  title?: string;
  dob?: string;
} {
  if ("date_of_birth" in subject) {
    // Dentist
    return {
      title: subject.title,
      name: subject.title + " " + subject.first_name + " " + subject.last_name,
      dob: subject.date_of_birth,
    };
  } else if ("finess" in subject) {
    // HealthCenter
    return {
      name: subject.name,
    };
  } else {
    return {
      title: subject.title,
      name: subject.title + " " + subject.first_name + " " + subject.last_name,
    };
  }
}

export const knownMacros = {
  $TITRE$:
    "Titre du protagoniste à qui le courrier est adressé (Monsieur, Madame, etc.)",
  $DESTINATAIRE$:
    "Le nom complet du protagoniste à qui le courrier est adressé (Monsieur Albert Dupont, Docteur Jeanne Durand, etc.)",
  $PRATICIEN$:
    "Nom complet du praticien associé au dossier (Docteur Jeanne Durand, etc.)",
  $PLAIGNANT$:
    "Nom complet du plaignant (requérant) associé au dossier (Monsieur Albert Dupont, Docteur Jeanne Durand, etc.)",
};

export function replaceMacros(
  template: string,
  params: {
    subject?: Actor | HealthCenter | Dentist;
    patient?: Actor;
    dentist?: Dentist;
  }
) {
  const notMatched = new Set<string>();
  const errors = new Set<string>();

  const info_subject: {
    name?: string;
    title?: string;
    dob?: string;
  } = params.subject ? extractInfo(params.subject) : {};
  const info_patient: {
    name?: string;
    title?: string;
    dob?: string;
  } = params.patient ? extractInfo(params.patient) : {};
  const info_dentist: {
    name?: string;
    title?: string;
    dob?: string;
  } = params.dentist ? extractInfo(params.dentist) : {};

  const foundMacros = template.match(templateRegexp);
  let currentText = template;

  if (foundMacros != null) {
    foundMacros.forEach((macro) => {
      if (macro in knownMacros) {
        if (macro === "$TITRE$") {
          if (info_subject.title) {
            currentText = currentText.replace(macro, info_subject.title);
          } else {
            notMatched.add(macro);
          }
        } else if (macro === "$DESTINATAIRE$") {
          if (info_subject.name) {
            currentText = currentText.replace(macro, info_subject.name);
          } else {
            notMatched.add(macro);
          }
        } else if (macro === "$PRATICIEN$") {
          if (info_dentist.name) {
            currentText = currentText.replace(macro, info_dentist.name);
          } else {
            notMatched.add(macro);
          }
        } else if (macro === "$PLAIGNANT$") {
          if (info_patient.name) {
            currentText = currentText.replace(macro, info_patient.name);
          } else {
            notMatched.add(macro);
          }
        }
      } else {
        errors.add(macro);
      }
    });
  }

  return { text: currentText, notMatched, errors };
}
